import { useCallback } from 'react'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'

import CampaignNameFields from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm/Steps/CampaignNameStep/CampaignNameFields'
import AdvancedSettings from './AdvancedSettings'
import UploadAdCreativeCampaignForm from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm'
import InventoryOverviewSpoiler from '../components/InventoryOverviewSpoiler'

import { usePurifiedFormik } from '../../../../../hooks/formHooks/usePurifiedFormik'
import { formatOptionsList } from '../../../../../features/formatters'

import { setRightUploadAdData } from '../../../../../modules/actions/rights'
import { createCampaign } from '../../../../../modules/actions/campaigns'
import { lineItemsLoadingSelector } from '../../../../../modules/selectors/lineItems'
import { campaignCreateIsLoadingSelector, campaignsSelector } from '../../../../../modules/selectors/campaigns'
import { selectedPartnershipSelector } from '../../../../../modules/selectors/partnerships'
import { selfAccountSelector } from '../../../../../modules/selectors/selfAccounts'

import { LIST_ITEM_OPTION, OPTION_EXISTING_LIST_ITEM } from '../../../../../hooks/formHooks/listItemFormHooks/fields'
import { CAMPAIGN } from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm/fields'
import {
  GOOGLE_AD_MANAGER_PLATFORM,
  GOOGLE_AD_MANAGER_PROVIDER_PLATFORM
} from '../../../../../constants/selectLists/platformList'
import { createCampaignPayload, getInitialValues, validationSchema } from './fields'

import {
  GAM_NETWORK_CODE,
  GAM_TRAFFICKER_ID
} from '../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/GamOnsiteRightCreateSections/fields'
import { GOOGLE_AD_MANAGER_AD_ACCOUNT } from '../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/fields'

const CampaignForm = ({ formProgressItems, onSuccessSubmit }) => {
  const dispatch = useDispatch()

  const campaigns = useSelector(campaignsSelector)
  const selectedPartnership = useSelector(selectedPartnershipSelector)
  const { [GOOGLE_AD_MANAGER_AD_ACCOUNT]: gamAccount } = selectedPartnership
  const gamAccountId = gamAccount?.external_id

  const selfAccount = useSelector(selfAccountSelector)
  const gamNetworkCode = selfAccount?.[GAM_NETWORK_CODE]
  const gamTraffickerId = selfAccount?.[GAM_TRAFFICKER_ID]

  const isCampaignsExist = !!campaigns.length

  const formattedCampaigns = formatOptionsList({
    list: campaigns,
    valueName: 'id',
    labelName: 'name'
  })

  const onSubmit = useCallback(
    values => {
      // if existing campaign is selected, find full data and set to redux state + fetch line items
      if (values[LIST_ITEM_OPTION] === OPTION_EXISTING_LIST_ITEM) {
        const selectedCampaignFullData = formattedCampaigns.find(campaign => values[CAMPAIGN] === campaign.id)

        dispatch(setRightUploadAdData({ campaign: selectedCampaignFullData }, GOOGLE_AD_MANAGER_PROVIDER_PLATFORM))
      } else {
        const formattedValues = createCampaignPayload({
          values,
          gamNetworkCode,
          selectedPartnership
        })

        const campaignData = {
          ...formattedValues,
          advertiser_id: gamAccountId,
          trafficker_id: gamTraffickerId
        }

        dispatch(createCampaign({ campaignData }, GOOGLE_AD_MANAGER_PLATFORM))
      }
    },
    [dispatch, formattedCampaigns, selectedPartnership, gamNetworkCode, gamTraffickerId, gamAccountId]
  )

  const formik = useFormik({
    initialValues: getInitialValues({ isCampaignsExist }),
    validationSchema: validationSchema,
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)
  const { values } = purifiedFormik

  const { [LIST_ITEM_OPTION]: campaignOption } = values

  // when we use existing campaign, we need to pre-load line items for the next LineItem form
  // in this case submit form loads line item. However, we don't do the same for error selectors,
  // because we don't block user for accessing next form when line items get request failed
  const isLoadingSelector =
    campaignOption === OPTION_EXISTING_LIST_ITEM ? lineItemsLoadingSelector : campaignCreateIsLoadingSelector

  const onSuccessCreateCampaignHandler = useCallback(() => {
    onSuccessSubmit && onSuccessSubmit()
  }, [onSuccessSubmit])

  return (
    <UploadAdCreativeCampaignForm
      adAccountId={gamNetworkCode}
      formik={formik}
      platform={GOOGLE_AD_MANAGER_PLATFORM}
      onSuccessSubmit={onSuccessCreateCampaignHandler}
      isLoadingSelector={isLoadingSelector}
      existedCampaigns={formattedCampaigns}
      formProgressItems={formProgressItems}
      allowShowNameStep={false}
      advancedSettingsChildren={<AdvancedSettings formik={formik} />}
      shouldClearCampaigns={false}
      campaignSelectionStepTopChildren={<InventoryOverviewSpoiler initialOpen={true} />}
      shouldFetchLineItems={false}
    >
      <CampaignNameFields formik={formik} />
    </UploadAdCreativeCampaignForm>
  )
}

export default CampaignForm
